/**
 * GuestProfileView - Simplified profile view for guest (anonymous) users
 * Shows benefits of creating an account and the registration form
 * that converts the anonymous account into a full account.
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Ghost, Star, Gift, Calendar, Smartphone } from 'lucide-react';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { EmailAuthProvider, linkWithCredential } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/lib/toast';
import LargeTextToggleCard from './LargeTextToggleCard';

export default function GuestProfileView() {
  const location = useLocation();
  const { t } = useTranslation();
  const { firebaseUser, signOut } = useAuth();

  // State for guest registration form - check if redirected from GuestRegistrationBanner
  const locationState = location.state as { openRegister?: boolean } | null;
  const [showRegisterForm, setShowRegisterForm] = useState(locationState?.openRegister ?? false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registering, setRegistering] = useState(false);

  // Handle guest registration - convert anonymous account to full account
  const handleGuestRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firebaseUser) {
      toast.error(t('profilePage.noActiveSession'));
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      toast.error(t('profilePage.passwordsDoNotMatch'));
      return;
    }

    if (registerPassword.length < 6) {
      toast.error(t('profilePage.passwordTooShort'));
      return;
    }

    if (!registerName.trim()) {
      toast.error(t('profilePage.nameRequired'));
      return;
    }

    setRegistering(true);
    const toastId = toast.loading(t('profilePage.creatingAccount'));

    try {
      // Link email/password credential to anonymous account
      const credential = EmailAuthProvider.credential(registerEmail, registerPassword);
      await linkWithCredential(firebaseUser, credential);

      // Update user document with new info
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        email: registerEmail,
        displayName: registerName.trim(),
        updatedAt: Timestamp.now(),
      }, { merge: true });

      // Call Cloud Function to update role and custom claims
      const upgradeGuestToClient = httpsCallable(functions, 'upgradeGuestToClient');
      await upgradeGuestToClient({});

      // Force token refresh to get new claims
      await firebaseUser.getIdToken(true);

      toast.success(t('profilePage.accountCreated'), { id: toastId });

      // Reload page to get new user data with updated claims
      window.location.reload();
    } catch (error: any) {
      console.error('Error registering guest:', error);
      if (error.code === 'auth/email-already-in-use') {
        toast.error(t('profilePage.emailInUse'), { id: toastId });
      } else if (error.code === 'auth/invalid-email') {
        toast.error(t('profilePage.invalidEmail'), { id: toastId });
      } else if (error.code === 'auth/weak-password') {
        toast.error(t('profilePage.weakPassword'), { id: toastId });
      } else {
        toast.error(error.message || t('errors.unknown'), { id: toastId });
      }
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* Guest Header */}
        <div className="bg-surface border border-hairline rounded-xl p-6 mb-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-surface-3 flex items-center justify-center text-ink-subtle mb-4">
            <Ghost className="w-9 h-9" aria-hidden="true" />
          </div>
          <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink mb-2">
            {t('profilePage.guestGreeting')}
          </h1>
          <p className="text-sm text-ink-muted mb-4">
            {t('profilePage.guestBrowsing')}
          </p>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30">
            <Ghost className="w-3.5 h-3.5" aria-hidden="true" /> {t('profilePage.guestBadge')}
          </span>
        </div>

        {/* Benefits of creating account */}
        <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
          <h3 className="font-sans font-semibold text-ink mb-3 text-center">
            {t('profilePage.whyCreateAccount')}
          </h3>
          <div className="text-sm text-ink-muted space-y-2.5">
            <p className="flex items-center gap-2.5">
              <Star className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
              <span>{t('profilePage.benefitPoints')}</span>
            </p>
            <p className="flex items-center gap-2.5">
              <Gift className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
              <span>{t('profilePage.benefitStamps')}</span>
            </p>
            <p className="flex items-center gap-2.5">
              <Calendar className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
              <span>{t('profilePage.benefitVIP')}</span>
            </p>
            <p className="flex items-center gap-2.5">
              <Smartphone className="w-4 h-4 text-ink-subtle shrink-0" aria-hidden="true" />
              <span>{t('profilePage.benefitHistory')}</span>
            </p>
          </div>
        </div>

        {/* Register Form */}
        {!showRegisterForm ? (
          <div className="space-y-4">
            <button
              onClick={() => setShowRegisterForm(true)}
              className="w-full py-4 px-6 bg-accent text-on-accent text-lg font-bold rounded-xl hover:bg-accent-hover transition-colors focus:outline-none focus:ring-2 focus:ring-focus"
            >
              {t('profilePage.createMyAccount')}
            </button>
            <button
              onClick={() => signOut()}
              className="w-full py-3 px-6 text-ink-muted hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
            >
              {t('profilePage.guestSignOut')}
            </button>
          </div>
        ) : (
          <div className="bg-surface border border-hairline rounded-xl p-6">
            <h2 className="font-sans text-xl font-semibold text-ink mb-4 text-center">
              {t('profilePage.createAccount')}
            </h2>
            <form onSubmit={handleGuestRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  {t('profilePage.fullName')}
                </label>
                <input
                  type="text"
                  value={registerName}
                  onChange={(e) => setRegisterName(e.target.value)}
                  placeholder={t('profilePage.yourName')}
                  required
                  className="w-full px-4 py-3 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  {t('profilePage.emailLabel')}
                </label>
                <input
                  type="email"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  placeholder={t('profilePage.emailPlaceholder')}
                  required
                  className="w-full px-4 py-3 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  {t('profilePage.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  placeholder={t('profilePage.minChars')}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-1">
                  {t('profilePage.confirmPasswordLabel')}
                </label>
                <input
                  type="password"
                  value={registerConfirmPassword}
                  onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                  placeholder={t('profilePage.repeatPassword')}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-hairline rounded-lg bg-surface-3 text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus focus:border-transparent"
                />
              </div>
              <div className="flex flex-col gap-3 pt-2">
                <button
                  type="submit"
                  disabled={registering}
                  className="w-full py-3 px-6 bg-accent text-on-accent font-bold rounded-lg hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-focus"
                >
                  {registering ? t('profilePage.creatingAccountBtn') : t('profilePage.createAccountBtn')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRegisterForm(false)}
                  className="w-full py-2 px-6 text-ink-muted hover:text-ink hover:bg-surface-2 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>

            <div className="mt-4 pt-4 border-t border-hairline">
              <p className="text-xs text-ink-subtle text-center">
                {t('profilePage.guestTicketsNote')}
              </p>
            </div>
          </div>
        )}

        {/* Appearance - Large Text Toggle (also for guests) */}
        <LargeTextToggleCard
          className="mt-6 bg-surface border border-hairline rounded-xl p-6"
        />
      </div>
    </div>
  );
}
